import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  IconButton,
  Typography
} from '@mui/material';
import { DataGridPremium, GridColDef, GridToolbarExport, itIT } from '@mui/x-data-grid-premium';
import TenantPage from 'components/common/TenantPage';
import { VersionsColumns } from 'components/common/enhanced/common-headers/versions';
import { useVersionsData } from '../hooks/useVersionsData';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LogsFilterForm from '../components/LogsFilterForm';
import ObjectVersionsPanel from '../components/ObjectVersionsPanel';
import VersionDialog from '../components/VersionDialog';

export const VersionsPage = () => {
  const translationPrefix = 'pages.versions';
  const { models, versions, rowCount, loading, paginationModel, setPaginationModel, handleFilter } =
    useVersionsData();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any>(null);
  const [isFiltersAccordionOpen, setIsFiltersAccordionOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerObj, setDrawerObj] = useState<{ modelName: string; objectId: string } | null>(null);
  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const { t } = useTranslation();

  const handleOpenDialog = (id: string) => {
    const version = versions.find((v) => v.id === Number(id));
    setSelectedVersion(version);
    setDialogOpen(true);
  };

  const toggleFiltersAccordion = () => {
    setIsFiltersAccordionOpen((prev) => !prev);
  };

  const handleCombinedFilter = (values: Record<string, any>) => {
    handleFilter(values);
    setIsFiltersAccordionOpen(false);
  };

  const columnsWithDrawer: GridColDef[] = VersionsColumns(
    handleOpenDialog,
    (modelName, objectId) => {
      setDrawerObj({ modelName, objectId });
      setDrawerOpen(true);
    }
  );

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <div className="flex justify-end items-center">
          <ButtonGroup>
            <Link to="deleted">
              <Button color="primary" size="small">
                {t(`${translationPrefix}.delete-versions`)}
              </Button>
            </Link>
            <Link to="movement">
              <Button color="primary" size="small">
                {t(`${translationPrefix}.movement-versions`)}
              </Button>
            </Link>
            <Link to="logging">
              <Button color="primary" size="small">
                {t(`${translationPrefix}.sap_sync_logging`)}
              </Button>
            </Link>
            <Link to="access-logging">
              <Button color="primary" size="small">
                {t(`${translationPrefix}.access-logging`)}
              </Button>
            </Link>
          </ButtonGroup>
        </div>
      }>
      <div className="mt-6">
        <Accordion
          className="bg-slate-50 my-8"
          onChange={toggleFiltersAccordion}
          expanded={isFiltersAccordionOpen}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" color="primary">
              <b>Filtri</b>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <LogsFilterForm models={models} onSubmit={handleCombinedFilter} />
          </AccordionDetails>
        </Accordion>

        <DataGridPremium
          density="compact"
          rows={versions}
          loading={loading}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          sx={{ border: 'none', height: '75vh' }}
          disableSelectionOnClick
          pagination
          paginationMode="server"
          rowCount={rowCount}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onPageChange={(page) => setPaginationModel((prev) => ({ ...prev, page }))}
          onPageSizeChange={(pageSize) =>
            setPaginationModel((prev) => ({ ...prev, page: 0, pageSize }))
          }
          components={{ Toolbar: GridToolbarExport }}
          columns={columnsWithDrawer}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </div>

      {selectedVersion && (
        <VersionDialog
          open={dialogOpen}
          onClose={() => {
            setSelectedVersion(null);
            setDialogOpen(false);
          }}
          versionId={selectedVersion?.id}
        />
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerObj(null);
          setDrawerTitle('');
        }}>
        <Box sx={{ width: 720, maxWidth: '92vw', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" color="primary">
              <b>{drawerTitle || t('pages.versions.object.view.title')}</b>
            </Typography>
            <IconButton
              onClick={() => {
                setDrawerOpen(false);
                setDrawerObj(null);
                setDrawerTitle('');
              }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 2 }} />
          {drawerObj && (
            <ObjectVersionsPanel
              modelName={drawerObj.modelName}
              objectId={drawerObj.objectId}
              onMetaChange={({ title, subtitle }) => setDrawerTitle(`${title} — ${subtitle}`)}
            />
          )}
        </Box>
      </Drawer>
    </TenantPage>
  );
};

export default VersionsPage;
